import { Component, HostListener, inject, signal } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { LoadingSpinnerComponent } from "../../../../shared/components/loading-spinner/loading-spinner.component";
import { v4 as uuid } from 'uuid';
import { uniqueFormArrayValidator } from '../../../../core/validators/form-array.validator';
import { catchError, combineLatest, of, switchMap } from 'rxjs';
import { TenantContextService } from '../../services/tenant-context.service';
import { CreateDataStoreRequest } from '../../tabs/data-lakes-tab/models/create-data-store.request';
import { GetDataStoreRequest } from '../../tabs/data-lakes-tab/models/get-data-store-request';
import { DataStore } from '../../tabs/data-lakes-tab/models/data-store.model';
import { ToastService } from '../../../../core/services/toast.service';
import { DataStoreService } from '../../tabs/data-lakes-tab/services/data-store.service';

@Component({
  selector: 'app-add-data-lake-dialog',
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    MatIconModule,
    MatTooltipModule,
    MatAutocompleteModule,
    LoadingSpinnerComponent
],
  templateUrl: './add-data-lake-dialog.component.html',
  styleUrl: './add-data-lake-dialog.component.css'
})
export class AddDataLakeDialogComponent {

  dataStoreService = inject(DataStoreService);
  tenantContext = inject(TenantContextService);
  toastService = inject(ToastService);
  
  addDataLakeForm: FormGroup;

  isSubmitting = signal<boolean>(false);

  constructor (private dialogRef: MatDialogRef<AddDataLakeDialogComponent>, private fb: FormBuilder) {
    this.addDataLakeForm = this.fb.group({
      // each data lake FormGroup must have an unique value when compared to each other
      dataLakes: this.fb.array([this.createDataLakeInput()],[uniqueFormArrayValidator]),
    });
  }

  get dataLakeInputs(): FormArray {
    return this.addDataLakeForm.get("dataLakes") as FormArray;
  }

  createDataLakeInput(): FormGroup {
    return this.fb.group({
      // FormGroups are given a UUID to provide robust index tracker to allow for deletion of FormGroups
      id: uuid(),
      value: ['', [
        Validators.required,
        Validators.pattern(/^[a-zA-Z0-9-]{3,63}$/)
      ]]
    });
  }

  addDataLake(): void {
    if (this.dataLakeInputs.length < 10) {
      this.dataLakeInputs.push(this.createDataLakeInput());
    }
  }

  removeDataLakeInput(index: number): void {
    if (this.dataLakeInputs.length > 1) {
      this.dataLakeInputs.removeAt(index);
    }
  }

  onSubmit(): void {
    if (this.addDataLakeForm.valid) {
      this.isSubmitting.set(true);

      const dataLakeNames: string[] = [];

      // get the data lake name (value) for each FormGroup inside the FromArray
      this.dataLakeInputs.controls.forEach((control: AbstractControl) => {
        const formGroup = control as FormGroup;
        dataLakeNames.push(formGroup.value.value);
      });

      const tenantId = this.tenantContext.tenant()?.tenantId;
      if (!tenantId) return;

      const requests: CreateDataStoreRequest[] = [];
      dataLakeNames.forEach(name => {
        const request: CreateDataStoreRequest = {
          storeName: name,
          tenantId: tenantId
        }
        requests.push(request);
      });

      const createCalls = requests.map((request) => 
        this.dataStoreService.createDataLake(request).pipe(
          switchMap((result: string) => {
            const getRequest: GetDataStoreRequest = {
              tenantId: tenantId,
              dataStoreId: result
            }
            return this.dataStoreService.getDataLake(getRequest).pipe(
              catchError(error => {
                return of({ error, failed: true, stage: "get", result});
              })
            )
          }),
          catchError(error => {
            return of({ error, failed: true, stage: "create", request});
          })
        )
      );

      combineLatest(createCalls).subscribe(results => {
        const createdDataLakes: DataStore[] = results.filter(
          (result): result is DataStore => !('failed' in result)
        );
        const failures = results.filter(result => 'failed' in result);
        if (failures.length > 0) {
          let failureMessage = "Failed to create for the following data lake/s: ";
          failures.forEach(failure => {
            if (failure.error.error.error === undefined)
            {
              failureMessage = "Internal Server Error. Please try again later."
            }
            else {
              failureMessage = failureMessage + "\n - " + failure.error.error.error;
            }
          });

          this.toastService.showError(failureMessage);
        }
        this.isSubmitting.set(false);
        this.dialogRef.close(createdDataLakes);
      });
    }
  }

  // display default browser popup on unloading page during form submission
  // does not warn navigating to another page of neuro utils as it is a SPA and would require a route guard for it
  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: BeforeUnloadEvent): void {
    if (this.isSubmitting()) {
      $event.preventDefault();
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
